import { QUERY_KEYS } from '@avanta/shared/constants/common';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useServerPaginatedTable } from '@avanta/shared/hooks/useServerPaginatedTable';
import { isEnterpriseAppointmentStatus } from '@avanta/shared/utils/appointments';
import { isLoadingNewData } from '@avanta/shared/utils/table';
import { useQuery } from '@tanstack/react-query';
import {
  type ColumnFiltersState,
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
  type Table,
  type Updater,
} from '@tanstack/react-table';
import { useState } from 'react';
import {
  type EmployeeCountFilters,
  useFetchEnterpriseEmployeesCount,
} from 'hooks/useFetchEnterpriseEmployeesCount';
import { EnterprisesService } from 'services/enterprises';
import { useEnterpriseId } from 'stores/user';
import { type EnterpriseAppointmentStatus } from 'types/appointments';
import { type Employee } from 'types/employees';
import { type GetEmployeesByEnterpriseParams } from 'types/enterprises';
import { getServerSort } from 'utils/table';

const DEFAULT_PAGE_SIZE = 8;
const FILTER_OPTION = {
  WITH_SESSION: 'withSession',
  WITHOUT_SESSION: 'withoutSession',
  WITHOUT_FILE: 'withoutFile',
} as const;
export type PatientListFilterKey = (typeof FILTER_OPTION)[keyof typeof FILTER_OPTION];

const COLUMN_SORT_BY: Record<string, GetEmployeesByEnterpriseParams['sortBy']> = {
  name: 'name',
  fileNumber: 'fileNumber',
  center: 'center',
};

const resetPageIndex = (previousPagination: {
  pageIndex: number;
  pageSize: number;
}): { pageIndex: number; pageSize: number } =>
  previousPagination.pageIndex === 0 ? previousPagination : { ...previousPagination, pageIndex: 0 };

const getHasUpcomingSession = (filter: PatientListFilterKey | undefined): boolean | undefined => {
  if (filter === FILTER_OPTION.WITH_SESSION) return true;
  if (filter === FILTER_OPTION.WITHOUT_SESSION) return false;
  return undefined;
};

const getSessionStatusFilter = (
  columnFilters: ColumnFiltersState
): EnterpriseAppointmentStatus | undefined => {
  const sessionStatusValue = columnFilters.find(filter => filter.id === 'sessionStatus')?.value;
  return typeof sessionStatusValue === 'string' && isEnterpriseAppointmentStatus(sessionStatusValue)
    ? sessionStatusValue
    : undefined;
};

export const useFetchInsuredUsers = (
  columns: ColumnDef<Employee>[],
  pageSize: number = DEFAULT_PAGE_SIZE
): {
  table: Table<Employee>;
  totalCount: number;
  selectedFilter: PatientListFilterKey | undefined;
  setFilter: (value: PatientListFilterKey | undefined) => void;
} => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedFilter, setSelectedFilter] = useState<PatientListFilterKey | undefined>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const enterpriseId = useEnterpriseId();
  const debouncedSearch = useDebounce(globalFilter, 500).trim();
  const search = debouncedSearch || undefined;
  const selectedSessionStatus = getSessionStatusFilter(columnFilters);
  const hasUpcomingSession = getHasUpcomingSession(selectedFilter);
  const hasActiveRecord = selectedFilter === FILTER_OPTION.WITHOUT_FILE ? false : undefined;
  const { sortBy, sortOrder } = getServerSort(sorting, COLUMN_SORT_BY);
  const listFilters: EmployeeCountFilters = {
    search,
    hasUpcomingSession,
    hasActiveRecord,
    sessionStatus: selectedSessionStatus,
  };
  const hasActiveFilters = Object.values(listFilters).some(value => value !== undefined);
  const resetPage = (): void => {
    setPagination(resetPageIndex);
  };

  const handleGlobalFilterChange = (updater: Updater<string>): void => {
    setGlobalFilter(updater);
    resetPage();
  };

  const setFilter = (value: PatientListFilterKey | undefined): void => {
    setSelectedFilter(value);
    resetPage();
  };

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    setSorting(updater);
    resetPage();
  };

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = updater => {
    setColumnFilters(updater);
    resetPage();
  };

  const countQuery = useFetchEnterpriseEmployeesCount(hasActiveFilters ? listFilters : undefined);
  const totalCount = countQuery.data ?? 0;

  const query = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_INSURED_USERS,
      enterpriseId,
      pagination.pageIndex,
      pagination.pageSize,
      search,
      hasUpcomingSession,
      hasActiveRecord,
      selectedSessionStatus,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      EnterprisesService.getEmployeesByEnterprise({
        enterpriseId,
        offset: pagination.pageIndex,
        limit: pagination.pageSize,
        ...listFilters,
        sortBy,
        sortOrder,
      }),
    select: response => response.data,
    placeholderData: previousData => previousData,
    enabled: !!enterpriseId,
  });

  const table = useServerPaginatedTable<Employee>({
    columns,
    data: query.data ?? [],
    totalCount,
    pagination,
    onPaginationChange: setPagination,
    globalFilter,
    onGlobalFilterChange: handleGlobalFilterChange,
    columnFilters,
    onColumnFiltersChange: handleColumnFiltersChange,
    sorting,
    onSortingChange: handleSortingChange,
    manualSorting: true,
    isLoadingData: isLoadingNewData(query) || isLoadingNewData(countQuery),
    pageCountKey: listFilters,
  });

  return {
    table,
    totalCount,
    selectedFilter,
    setFilter,
  };
};
